/**
 * Export and import wizard configuration buttons
 */

import { Button } from '@/components/ui/button';
import { Download, Upload, Share2 } from 'lucide-react';
import { wizardStore } from '@/stores/wizardStore';
import { useUrlState } from '@/hooks/useUrlState';
import { toast } from '@/hooks/use-toast';
import { useRef } from 'react';

export function ExportImportButtons() {
  const { copyShareUrl } = useUrlState();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = () => {
    try {
      const state = wizardStore.getState();
      const exportData = {
        version: '1.0',
        exported: new Date().toISOString(),
        environment: state.environment,
        personas: state.personas,
        modelSelections: state.modelSelections,
        progress: state.progress,
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wizard-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: 'Configuration Exported',
        description: 'Your configuration has been downloaded as JSON',
      });
    } catch (err) {
      console.error('Export failed:', err);
      toast({
        title: 'Export Failed',
        description: 'Could not export configuration',
        variant: 'destructive',
      });
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const importData = JSON.parse(text);

      // Validate structure
      if (!importData.environment || !importData.personas || !importData.modelSelections) {
        throw new Error('Invalid configuration file');
      }

      wizardStore.setState({
        ...wizardStore.getState(),
        environment: importData.environment,
        personas: importData.personas,
        modelSelections: importData.modelSelections,
        progress: importData.progress || wizardStore.getState().progress,
      });

      toast({
        title: 'Configuration Imported',
        description: 'Successfully loaded configuration from file',
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Import failed:', err);
      toast({
        title: 'Import Failed',
        description: 'Could not read configuration file. Please check the file format.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <Button variant="outline" size="sm" onClick={handleImport} className="gap-2">
        <Upload className="h-4 w-4" />
        Import
      </Button>
      
      <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
        <Download className="h-4 w-4" />
        Export
      </Button>
      
      <Button variant="outline" size="sm" onClick={copyShareUrl} className="gap-2">
        <Share2 className="h-4 w-4" />
        Share Link
      </Button>
    </div>
  );
}
